import { plb, text, year } from './columnSpec.types';

import type { HoursColumnSpec } from './columnSpec.types';

/**
 * Ported 1:1 from `EmployeeWorkAgreementsGridColumns.js:156-577` (the always-present groups).
 * Order matters — it is the column order.
 *
 * `defaultVisible` reproduces Dojo's collapsed state: the columns that carried no `*DetailsCell`
 * class, i.e. what a user saw before expanding anything.
 */
export const CORE_HOURS_COLUMNS: HoursColumnSpec[] = [
  // Period — GridColumns.js:158-187
  {
    id: 'period',
    source: 'valid',
    group: 'period',
    label: text('month'),
    render: 'period',
    defaultVisible: true,
    width: 72
  },

  // Contract — :189-226
  {
    id: 'hoursContract',
    source: 'hoursContract',
    group: 'contract',
    label: text('hoursContract'),
    tooltipKey: 'hoursContract',
    defaultVisible: true
  },
  {
    id: 'hoursExtra',
    source: 'hoursExtra',
    group: 'contract',
    label: text('hoursExtra'),
    tooltipKey: 'hoursExtra',
    defaultVisible: true
  },
  {
    id: 'hoursCompLeave',
    source: 'hoursCompLeave',
    group: 'contract',
    label: text('hoursCompLeave'),
    tooltipKey: 'hoursCompLeave',
    defaultVisible: true
  },
  {
    id: 'empPaidHours',
    source: 'empPaidHours',
    group: 'contract',
    label: text('empPaidHours'),
    tooltipKey: 'empPaidHours',
    defaultVisible: true
  },

  // Contract details — :228-261
  {
    id: 'hoursContractMax',
    source: 'hoursContractMax',
    group: 'contractDetails',
    label: text('hoursContractMax')
  },
  {
    id: 'hoursUnpaid',
    source: 'hoursUnpaid',
    group: 'contractDetails',
    label: text('hoursUnpaid')
  },
  {
    id: 'inputWorkingHoursCor',
    source: 'inputWorkingHoursCor',
    group: 'contractDetails',
    label: text('inputWorkingHoursCor'),
    // the detail window shows this one with the balance it corrects instead — see hoursBalance below
    gridOnly: true
  },
  {
    id: 'balanceBudgetHours',
    source: 'balanceBudgetHours',
    group: 'contractDetails',
    label: text('balanceBudgetHours')
  },

  // Delivering on contract — :119-154 (deployedHoursChildren) + :262-267
  {
    id: 'hoursDeployed',
    source: 'hoursDeployed',
    group: 'delivering',
    label: text('hoursDeployed'),
    tooltipKey: 'hoursDeployed',
    defaultVisible: true
  },
  {
    id: 'hoursIll',
    source: 'hoursIll',
    group: 'delivering',
    label: text('hoursIll'),
    tooltipKey: 'hoursIll',
    defaultVisible: true
  },
  {
    id: 'hoursHolidayTotal',
    source: 'hoursHolidayTotal',
    group: 'delivering',
    label: text('hoursHolidayTotal'),
    tooltipKey: 'hoursHolidayTotal',
    defaultVisible: true
  },
  {
    id: 'hoursPlbUsedDirect',
    source: 'hoursPlbUsedDirect',
    group: 'delivering',
    label: plb('hoursPlbUsedDirect'),
    tooltipKey: 'hoursPlbUsedDirect',
    requiresFlag: 'PLB_VISIBILITY',
    defaultVisible: true
  },

  // Delivering on contract, details — :269-302
  // Dojo omitted the plbName replacement here (:274) so this header kept a literal "PLB";
  // interpolated for consistency with the rest of the budget-named columns.
  {
    id: 'hoursPlbTotalWork',
    source: 'hoursPlbTotalWork',
    group: 'deliveringDetails',
    label: plb('hoursPlbTotalWork')
  },
  {
    id: 'hoursJusBalanceCum:delivering',
    source: 'hoursJusBalanceCum',
    group: 'deliveringDetails',
    label: text('hoursJusBalanceCum'),
    signFlipWhenPlusMinus: true
  },
  {
    id: 'hoursHolidayContractMin',
    source: 'hoursHolidayContractMin',
    group: 'deliveringDetails',
    label: text('hoursHolidayContractMin')
  },
  {
    id: 'socialSecurityDayMonth',
    source: 'socialSecurityDayMonth',
    group: 'deliveringDetails',
    label: text('socialSecurityDayMonth')
  },

  // Hours balance — :304-342.
  // The first three rows exist only in the detail window: Dojo kept them in the correction form
  // (`EmployeeHoursCorrectionForm.js:34-132`), which read as base + correction = total.
  {
    id: 'hoursBalanceBase',
    source: 'hoursBalanceBase',
    group: 'hoursBalance',
    label: text('corrections.cumulativeBeforeCorrection'),
    signFlipWhenPlusMinus: true,
    detailOnly: true
  },
  {
    id: 'inputWorkingHoursCor:hoursBalance',
    source: 'inputWorkingHoursCor',
    group: 'hoursBalance',
    label: text('corrections.correctionHours'),
    signFlipWhenPlusMinus: true,
    detailOnly: true
  },
  {
    // computed, not a payload field: the balance before correction plus the correction being typed.
    // `source` is that base; the cell renderer adds the live correction to it.
    id: 'allHoursWorkTotal',
    source: 'hoursBalanceBase',
    group: 'hoursBalance',
    label: text('corrections.totalHoursStartValue'),
    render: 'totalHours',
    detailOnly: true
  },
  {
    id: 'inputJus',
    source: 'inputJus',
    group: 'hoursBalance',
    label: text('inputJus'),
    tooltipKey: 'inputJus',
    signFlipWhenPlusMinus: true
  },
  {
    id: 'hoursJusBalanceCum',
    source: 'hoursJusBalanceCum',
    group: 'hoursBalance',
    label: text('yearHours'),
    tooltipKey: 'yearHours',
    signFlipWhenPlusMinus: true
  },
  {
    id: 'hoursBalance',
    source: 'hoursBalance',
    group: 'hoursBalance',
    label: text('hoursBalance'),
    tooltipKey: 'hoursBalance',
    render: 'balance',
    defaultVisible: true
  },
  {
    id: 'hoursBalanceCumReal',
    source: 'hoursBalanceCumReal',
    group: 'hoursBalance',
    label: text('hoursBalanceCumReal'),
    tooltipKey: 'hoursBalanceCumReal',
    render: 'cumulativeBalance',
    defaultVisible: true
  },

  // Hours balance details — :346-357
  {
    id: 'hoursBalanceCum',
    source: 'hoursBalanceCum',
    group: 'hoursBalanceDetails',
    label: text('hoursBalanceCum')
  },

  // Leave — :361-381
  {
    id: 'empLeaveHours',
    source: 'empLeaveHours',
    group: 'leave',
    label: text('empLeaveHours'),
    tooltipKey: 'empLeaveHours',
    defaultVisible: true
  },
  {
    id: 'hoursHolidayBalanceCum',
    source: 'hoursHolidayBalanceCum',
    group: 'leave',
    label: text('hoursHolidayBalanceCum'),
    tooltipKey: 'hoursHolidayBalanceCum',
    defaultVisible: true
  },

  // Leave, structure — :385-424
  {
    id: 'empVacationHours',
    source: 'empVacationHours',
    group: 'leaveStructure',
    label: text('empVacationHours')
  },
  {
    id: 'empAgeHours2',
    source: 'empAgeHours2',
    group: 'leaveStructure',
    label: text('empAgeHours2')
  },
  {
    id: 'empHolidayHours',
    source: 'empHolidayHours',
    group: 'leaveStructure',
    label: text('empHolidayHours')
  },
  {
    id: 'empLeaveHoursPrognosis',
    source: 'empLeaveHoursPrognosis',
    group: 'leaveStructure',
    label: text('empLeaveHoursPrognosis')
  },
  {
    id: 'deploymentRatio',
    source: 'deploymentRatio',
    group: 'leaveStructure',
    label: text('deploymentRatio')
  },

  // Leave, correction — :427-452
  {
    id: 'leaveCorrection',
    source: 'leaveCorrection',
    group: 'leaveCorrection',
    label: text('allHoursLvCorTotal')
  },
  {
    id: 'hoursPlbTotalLv',
    source: 'hoursPlbTotalLv',
    group: 'leaveCorrection',
    label: plb('hoursPlbTotalLv')
  },
  {
    id: 'hoursCompLeave:leaveCorrection',
    source: 'hoursCompLeave',
    group: 'leaveCorrection',
    label: text('hoursCompLeave')
  },

  // Leave, record — :455-494
  {
    id: 'hoursHolidayComp',
    source: 'hoursHolidayComp',
    group: 'leaveRecord',
    label: text('hoursHolidayComp')
  },
  {
    id: 'hoursHoliday',
    source: 'hoursHoliday',
    group: 'leaveRecord',
    label: text('hoursHoliday')
  },
  {
    id: 'hoursHolidayTotal:leaveRecord',
    source: 'hoursHolidayTotal',
    group: 'leaveRecord',
    label: text('hoursHolidayTotal')
  },
  {
    id: 'hoursLeavePaid',
    source: 'hoursLeavePaid',
    group: 'leaveRecord',
    label: text('hoursLeavePaid')
  },
  {
    id: 'hoursLeavePaidEndEmpl',
    source: 'hoursLeavePaidEndEmpl',
    group: 'leaveRecord',
    label: text('hoursLeavePaidEndEmpl')
  },

  // Leave, statutory — :497-513
  {
    id: 'hoursLeaveMin',
    source: 'hoursLeaveMin',
    group: 'leaveStatutory',
    label: year(0, 'hoursLeaveMin')
  },
  {
    id: 'leaveMinPrev',
    source: 'leaveMinPrev',
    group: 'leaveStatutory',
    label: year(1, 'leaveMinPrev')
  },

  // Leave, compensation — :516-526
  {
    id: 'hoursCompLeaveAll',
    source: 'hoursCompLeaveAll',
    group: 'leaveCompensation',
    label: text('compensation')
  },

  // Leave, extra-legal — :529-575
  {
    id: 'leaveOver',
    source: 'leaveOver',
    group: 'leaveExtralegal',
    label: year(0, 'leaveOver')
  },
  {
    id: 'leaveOverPrev',
    source: 'leaveOverPrev',
    group: 'leaveExtralegal',
    label: year(1, 'leaveOverPrev')
  },
  {
    id: 'overLeaveAge1',
    source: 'overLeaveAge1',
    group: 'leaveExtralegal',
    label: year(2, 'overLeaveAge1')
  },
  {
    id: 'overLeaveAge2',
    source: 'overLeaveAge2',
    group: 'leaveExtralegal',
    label: year(3, 'overLeaveAge2')
  },
  {
    id: 'overLeaveAge3',
    source: 'overLeaveAge3',
    group: 'leaveExtralegal',
    label: year(4, 'overLeaveAge3')
  },
  {
    id: 'overLeaveAge4',
    source: 'overLeaveAge4',
    group: 'leaveExtralegal',
    label: year(5, 'overLeaveAge4')
  },
  {
    id: 'overLeaveAge5',
    source: 'overLeaveAge5',
    group: 'leaveExtralegal',
    label: year(6, 'overLeaveAge5', { andOlder: true })
  },
  {
    id: 'leaveOneTimeDepositRato',
    source: 'leaveOneTimeDepositRato',
    group: 'leaveExtralegal',
    label: text('corrections.oneTimeAllowance'),
    detailOnly: true
  }
];
